import {
  AUDIENCE_TYPES,
  AUDIENCE_TYPES_ARRAY,
  getAudienceEmoji,
  getAudienceSlug,
} from "posts/constants";
import { FullWidthContainer } from "components/PageContainer";
import { FeedDispatchContext, FeedStateContext } from "context/feed";
import React, { useEffect, useState } from "react";
import WelcomeMessage, {
  WELCOME_MESSAGE_FEED,
} from "components/WelcomeMessage";
import { feedInitialState, feedReducer } from "context/feed";

import AudienceEmoji from "components/AudienceEmoji";
import { Button } from "react-bootstrap";
import Feed from "components/Feed";
import FeedFilters from "components/FeedFilters";
import Head from "next/head";
import Nav from "components/Nav/Nav";
import SecondaryNav from "components/Nav/SecondaryNav";
import ZeroState from "components/ZeroState";
import Link from "next/link";
import { getAudienceTypeColor } from "utils/styles";
import isEqual from "lodash.isequal";
import styled, { createGlobalStyle } from "styled-components";
import { useReducer } from "react";
import {
  collapseAnimationDuration,
  collapseAnimationEasing,
} from "components/Feed/styles";

const GlobalStyle = createGlobalStyle`
body{
  background: #fff;
}`;

const FeedPageWrapper = styled.div``;

const CollapseButton = styled(Button).attrs({
  variant: "white",
  className: "d-none d-sm-block",
})`
  border-radius: 15px;

  & > i {
    padding: 9px 2px 7px 2px;
  }
`;

const KEYCODE_LEFT_ARROW = 37;
const KEYCODE_RIGHT_ARROW = 39;

export default function FeedPage({ posts, baseUrl }) {
  const [state, dispatch] = useReducer(feedReducer, feedInitialState);
  const currentAudience = state.audience;
  const [activeAudience, setActiveAudience] = useState();
  const [activeNarratives, setActiveNarratives] = useState();
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    dispatch({
      type: "set-audience",
      payload: currentAudience,
    });
  }, []);

  const updateActiveAudience = (audience) => {
    if (!isEqual(audience, state.audience)) {
      dispatch({
        type: "set-audience",
        payload: audience,
      });
    }
    switch (audience) {
      case AUDIENCE_TYPES.PEOPLE_POWER:
        window.history.replaceState({}, null, "/feed/people-power");
        break;
      case AUDIENCE_TYPES.IF_YOU_SAY_SO:
        window.history.replaceState({}, null, "/feed/if-you-say-so");
        break;
      case AUDIENCE_TYPES.TOUGH_COOKIES:
        window.history.replaceState({}, null, "/feed/tough-cookies");
        break;
      case AUDIENCE_TYPES.DONT_TREAD_ON_ME:
        window.history.replaceState({}, null, "/feed/dont-tread-on-me");
        break;
    }
  };

  const updateActiveNarratives = (narratives) => {
    if (!isEqual(narratives, state.narratives)) {
      dispatch({
        type: "set-narratives",
        payload: narratives,
      });
    }
  };

  useEffect(() => {
    // On page load, we first want to pull audience/narrative state from the
    // url. If it doesn't exist there, (i.e. this is someone's first visit from
    // a direct link to this page), intiialize the state with no filters (and
    // we can either show everything or nothing).
    const params = new URLSearchParams(window.location.search);
    if (params.has("audience")) {
      const a = params.get("audience");
      if (a.length) {
        updateActiveAudience(a);
      } else {
        updateActiveAudience();
      }
    }

    if (params.has("povertyNarratives")) {
      const n = params.get("povertyNarratives");
      if (n.length) {
        updateActiveNarratives(n.split(","));
      } else {
        updateActiveNarratives([]);
      }
    }

    // Also on page load, we need to populate the initial set of posts.
    const sortedPosts = posts
      .filter((p) => p.added)
      .sort((a, b) => b.added - a.added);
    dispatch({
      type: "set-posts",
      payload: sortedPosts,
    });
  }, []);

  const handleChangeAudience = (delta) => {
    const currentIndex = AUDIENCE_TYPES_ARRAY.indexOf(state.audience);
    let newIndex = (currentIndex + delta) % AUDIENCE_TYPES_ARRAY.length;
    if (newIndex < 0) {
      newIndex = AUDIENCE_TYPES_ARRAY.length + newIndex;
    }
    updateActiveAudience(AUDIENCE_TYPES_ARRAY[newIndex]);
  };

  const handleWindowKeydown = (e) => {
    if (e.keyCode === KEYCODE_LEFT_ARROW) {
      handleChangeAudience(-1);
    } else if (e.keyCode === KEYCODE_RIGHT_ARROW) {
      handleChangeAudience(1);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleWindowKeydown);
    return () => {
      window.removeEventListener("keydown", handleWindowKeydown);
    };
  }, [state.audience]);

  const getOgImage = (currentAudience) => {
    switch (currentAudience) {
      case AUDIENCE_TYPES.PEOPLE_POWER:
        return "open-graph-people-power.png";
      case AUDIENCE_TYPES.IF_YOU_SAY_SO:
        return "open-graph-if-you-say-so.png";
      case AUDIENCE_TYPES.TOUGH_COOKIES:
        return "open-graph-tough-cookies.png";
      case AUDIENCE_TYPES.DONT_TREAD_ON_ME:
        return "open-graph-dont-tread-on-me.png";
      default:
        return "open-graph.png";
    }
  };

  return (
    <FeedStateContext.Provider value={state}>
      <FeedDispatchContext.Provider value={dispatch}>
        <Head>
          <title>
            Media Feed
            {state && state.audience ? ` | ${state.audience}` : ""}
          </title>

          <link rel="icon" href="/favcon2.png" />
          <meta
            property="og:image"
            content={`${baseUrl}/images/${getOgImage(currentAudience)}`}
          />
          <meta
            property="twitter:image"
            content={`${baseUrl}/images/${getOgImage(currentAudience)}`}
          />
        </Head>

        <main>
          <Nav
            isCollapsed={isCollapsed}
            breadCrumbs={[
              <a href="/feed">Stories &amp; Opportunities</a>,

              currentAudience && (
                <a href={`/feed/${getAudienceSlug(currentAudience)}`}>
                  {getAudienceEmoji(currentAudience)}&nbsp;{currentAudience}
                </a>
              ),
            ].filter((link) => link !== null)}
            feedIsActive
            secondaryNav={
              <>
                <CollapseButton
                  onClick={() => {
                    setIsCollapsed(!isCollapsed);
                  }}
                  $isCollapsed={isCollapsed}
                >
                  <i className="material-icons-round md-18">
                    {isCollapsed ? "last_page" : "first_page"}
                  </i>
                </CollapseButton>
                <FeedFilters />
              </>
            }
          />
          <FeedPageWrapper>
            <Feed
              isCollapsed={isCollapsed}
              currentAudience={currentAudience}
              filters={state.filters}
              posts={state.activePosts}
            />
          </FeedPageWrapper>
          <SecondaryNav />
        </main>
        <GlobalStyle />
      </FeedDispatchContext.Provider>
    </FeedStateContext.Provider>
  );
}
